import React, { useState } from "react";
import ErrataDisplay from "/src/helpers/components/errataDisplay.jsx";
import CancelledStampOverlay from "/src/helpers/components/cancelledStampOverlay.jsx";

const formatGbp = (value) =>
    typeof value === "number"
        ? `£${value.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        : null;

const CarIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 13l2-5a2 2 0 011.9-1.4h10.2A2 2 0 0119 8l2 5m-18 0h18m-18 0v4a1 1 0 001 1h1a1 1 0 001-1v-1h12v1a1 1 0 001 1h1a1 1 0 001-1v-4M6.5 16.5h.01M17.5 16.5h.01" />
    </svg>
);

const HomepageCarHireRow = ({ item }) => {
    const [expanded, setExpanded] = useState(false);
    const isCancelled = item?.cancelled === true;
    const pickupDate = item.start ? new Date(item.start) : null;
    const dropoffDate = item.end ? new Date(item.end) : null;
    const isValidDate = (d) => d && d.getFullYear() > 1;
    const hasGuests = item.guests && item.guests.length > 0;
    const hasErratas = item.erratas && item.erratas.length > 0;

    const formatDate = (date) =>
        date.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" });

    const carChips = (item.carInformation || "")
        .split("|")
        .map((s) => s.trim())
        .filter((s) => s && !/^inclusions/i.test(s))
        .slice(0, 4);

    const Depot = ({ label, name, time, info }) => (
        <div className="flex-1">
            <p className="text-xs font-semibold text-content_3 uppercase tracking-wide">{label}</p>
            <p className="text-sm font-medium text-primary">{name}</p>
            {time && <p className="text-xs text-content_3">{time}</p>}
            {info && (info.phone || info.address1) && (
                <p className="text-xs text-content_4 mt-1">
                    {[info.address1, info.city, info.postcode].filter(Boolean).join(", ")}
                    {info.phone ? ` · ${info.phone}` : ""}
                </p>
            )}
        </div>
    );

    return (
        <div className={`relative rounded-card shadow-card ${(hasGuests || hasErratas) ? "px-8 pt-6 pb-2" : "px-8 py-6"} ${isCancelled ? "bg-red-50 border border-red-200" : "bg-white"}`}>
            {isCancelled && <CancelledStampOverlay />}

            <div className="flex flex-wrap items-center gap-2 mb-3">
                <div className="text-emp"><CarIcon /></div>
                <span className="text-sm font-semibold text-content_2 uppercase tracking-wide">Car Hire</span>
                {item.vehicleDescription && (
                    <span className="text-xs bg-emp_soft text-emp_dark font-medium px-2.5 py-1 rounded-full">{item.vehicleDescription}</span>
                )}
                {item.isAncillary && (
                    <span className="text-xs bg-emp_deep text-white font-medium px-2.5 py-1 rounded-full">
                        Extras booking {item.ancillaryReference}
                    </span>
                )}
                {formatGbp(item.totalPrice) && (
                    <span className="text-sm font-semibold text-primary ml-auto">{formatGbp(item.totalPrice)}</span>
                )}
                {item.supplierConfirmation && (
                    <span className="text-xs font-medium text-content_2 basis-full">Confirmation: {item.supplierConfirmation}</span>
                )}
                {/* Only a cancellation *fee* is worth a badge; "free cancellation" is not advertised. */}
                {item.cancellable === true && item.cancellationCharge > 0 && (
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-orange_light text-orange">
                        Cancellation fee {formatGbp(item.cancellationCharge)}
                    </span>
                )}
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
                {item.vehicleImage && (
                    <img src={item.vehicleImage} alt={item.vehicleDescription || "Hire vehicle"}
                        className="w-full sm:w-40 h-28 object-contain rounded-lg bg-content_6/30 shrink-0" />
                )}
                <div className="flex-1">
                    <div className="flex flex-col sm:flex-row gap-3">
                        <Depot
                            label="Pick up"
                            name={item.pickupDepotName}
                            time={isValidDate(pickupDate) ? `${formatDate(pickupDate)}${item.pickupTime ? ` · ${item.pickupTime}` : ""}` : null}
                            info={item.pickupInformation}
                        />
                        <Depot
                            label="Drop off"
                            name={item.dropOffDepotName}
                            time={isValidDate(dropoffDate) ? `${formatDate(dropoffDate)}${item.dropOffTime ? ` · ${item.dropOffTime}` : ""}` : null}
                            info={item.dropOffInformation}
                        />
                    </div>
                    {carChips.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-3">
                            {carChips.map((chip, i) => (
                                <span key={i} className="text-xs bg-content_6/40 text-content_2 px-2 py-0.5 rounded">{chip}</span>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {hasErratas && (
                <div className="mt-3 pt-3 border-t border-content_5">
                    <ErrataDisplay erratas={item.erratas} />
                </div>
            )}

            {hasGuests && (
                <>
                    <button
                        onClick={() => setExpanded(!expanded)}
                        className="flex items-center gap-2 mt-3 pt-3 border-t border-content_5 w-full text-left cursor-pointer"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-content_3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        <span className="text-sm font-medium text-content_2">{item.guests.length} Driver{item.guests.length !== 1 ? "s" : ""} / Guest{item.guests.length !== 1 ? "s" : ""}</span>
                        <svg xmlns="http://www.w3.org/2000/svg"
                            className={`h-4 w-4 text-content_3 ml-auto transition-transform duration-200 ${expanded ? "rotate-180" : ""}`}
                            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                        </svg>
                    </button>
                    {expanded && (
                        <div className="flex flex-wrap gap-2 mt-3">
                            {item.guests.map((guest, gIdx) => (
                                <div key={gIdx} className="flex items-center gap-2 text-sm text-content_1">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-content_3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                    </svg>
                                    <span>{guest.title} {guest.firstname} {guest.lastname}</span>
                                    {guest.type && (
                                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                                            guest.type === "Adult" ? "bg-emp_soft text-emp_dark" :
                                            guest.type === "Child" ? "bg-orange_light text-orange" :
                                            "bg-ocean-100 text-ocean-700"
                                        }`}>{guest.type}</span>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default HomepageCarHireRow;
